const LENGTH_PARAMS = new Set([
  "position",
  "length",
  "width",
  "first",
  "second",
  "left",
  "right",
  "above",
  "below",
]);

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {Record<string, unknown>} params
 */
function formatWorkshopParams(display, params = {}) {
  const formatted = { ...params };

  if (typeof formatted.orientationKey === "string") {
    formatted.orientation = display.t(`workshop.orientation.${formatted.orientationKey}`);
    delete formatted.orientationKey;
  }

  if (typeof formatted.orientationCapKey === "string") {
    formatted.orientation = display.t(`workshop.orientationCap.${formatted.orientationCapKey}`);
    delete formatted.orientationCapKey;
  }

  if (typeof formatted.spanKey === "string") {
    formatted.span = display.t(`workshop.span.${formatted.spanKey}`);
    delete formatted.spanKey;
  }

  if (
    typeof formatted.sizeLength === "number"
    && typeof formatted.sizeWidth === "number"
  ) {
    formatted.size = display.formatPanelSize(formatted.sizeLength, formatted.sizeWidth);
    delete formatted.sizeLength;
    delete formatted.sizeWidth;
  }

  for (const [key, value] of Object.entries(formatted)) {
    if (typeof value === "number" && LENGTH_PARAMS.has(key)) {
      formatted[key] = display.formatLength(value);
    }
  }

  return formatted;
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {{ key: string, params?: Record<string, unknown> }|null|undefined} descriptor
 */
export function resolveWorkshopMessage(display, descriptor) {
  if (!descriptor) {
    return null;
  }

  return display.t(descriptor.key, formatWorkshopParams(display, descriptor.params));
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {{
 *   title: { key: string, params?: Record<string, unknown> },
 *   detail?: { key: string, params?: Record<string, unknown> }|null,
 *   detailParts?: { key: string, params?: Record<string, unknown> }[],
 * }} instruction
 */
export function resolveWorkshopInstruction(display, instruction) {
  const title = resolveWorkshopMessage(display, instruction.title) ?? "";
  const detailParts = instruction.detailParts?.length
    ? instruction.detailParts
      .map((part) => resolveWorkshopMessage(display, part))
      .filter(Boolean)
    : [];

  if (detailParts.length > 0) {
    return {
      title,
      detail: detailParts.join(" "),
    };
  }

  return {
    title,
    detail: resolveWorkshopMessage(display, instruction.detail),
  };
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {{ messages: { title: object, detail?: object|null, detailParts?: object[], stepLabel: object } }} stepState
 */
export function resolveWorkshopStepState(display, stepState) {
  const { messages } = stepState;
  const resolved = resolveWorkshopInstruction(display, messages);

  return {
    ...stepState,
    title: resolved.title,
    detail: resolved.detail ?? null,
    stepLabel: resolveWorkshopMessage(display, messages.stepLabel) ?? "",
  };
}

/**
 * @param {ReturnType<import("./createDisplay.js").createDisplay>} display
 * @param {number} currentStep
 * @param {number} maxStep
 */
export function formatWorkshopStepProgress(display, currentStep, maxStep) {
  const label = currentStep === 0
    ? display.t("workshop.stepLabel.start")
    : display.t("workshop.stepLabel.progressCut", { current: currentStep });

  return display.t("mobile.workshop.stepProgress", { label, max: maxStep });
}
